import { AccountLevelEnum, SnackBarVariants } from '@app/app.enum';
import { history } from '@app/app.routes';
import store from '@app/app.store';
import { withAxiosCancellation } from '@app/components/core/axios-cancel.component';
import * as models from '@app/models/index';
import { Module } from '@app/models/index';
import Injector from '@app/services';
import { AccountService } from '@app/services/account.service';
import { AdminCarrierAccountService } from '@app/services/admin-carrier-account.service';
import { CarrierAccountService } from '@app/services/carrier-account.service';
import { IntlService } from '@app/services/intl.service';
import { SharedService } from '@app/services/shared.service';
import { CircularProgress, Grid, InputAdornment, TextField } from '@material-ui/core';
import Axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import withSnackBar from '../core/message.component';
import Icon from '../shared/icon.component';
import CarrierCardComponent from './carrier-card.component';

const accountService: AccountService = Injector.get(AccountService);
const adminCarrierAccountService: AdminCarrierAccountService = Injector.get(AdminCarrierAccountService);
const carrierAccountService: CarrierAccountService = Injector.get(CarrierAccountService);
const sharedService: SharedService = Injector.get(SharedService);
const intlService: IntlService = Injector.get(IntlService);

let carriers: models.ICarrierAccounts[] = [];

const CarrierViewComponent: React.FunctionComponent<models.ICarrierViewProps> = (props) => {
	const userApps = accountService.getUserData().apps;
	const [userCarriersList, setUserCarriersList] = useState<models.ICarrierAccounts[]>([]);
	const [deletedCarrierId, setDeletedCarrierId] = useState<string>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [searchValue, setSearchValue] = useState<string>('');
	const [applicableCarriers, setApplicableCarriers] = useState<any>(null);
	const [routedFrom, setRoutedFrom] = useState<string>(props.isRoutedFrom);
	const isPartnerAccount = store.getState().authentication.user.account.level === AccountLevelEnum.Partner;

	// Cleanup effect after component unmounts
	useEffect(
		() => () => {
			carriers = [];
		},
		[]
	);

	// Used for fetching all carrier accounts when navigating through sidebar
	useEffect(() => {
		async function GetAllAddedCarrierAccounts() {
			try {
				setLoading(true);
				const res = await carrierAccountService.GetCarrierAccounts(
					props.appId,
					props.axiosCancellationToken,
					props.fetchAll
				);
				updateList(res);
				setLoading(false);
			} catch (err) {
				if (!Axios.isCancel(err)) {
					props.openSnackBar(sharedService.parseError(err.data), SnackBarVariants.Error);
				}
			}
		}
		if (props.fetchAll) {
			GetAllAddedCarrierAccounts();
		}
	}, [props.fetchAll]);

	// Used for fetching all carrier accounts when navigating through App component
	useEffect(() => {
		async function GetAddedCarrierAccounts() {
			try {
				let res;
				setLoading(true);
				if (!accountService.IsAdmin() && !isPartnerAccount) {
					res = await carrierAccountService.GetCarrierAccounts(props.appId, props.axiosCancellationToken);
				} else {
					res = await adminCarrierAccountService.GetCarrierAccounts(props.appId, props.axiosCancellationToken);
				}
				updateList(res);
				setLoading(false);
			} catch (err) {
				if (!Axios.isCancel(err)) {
					props.openSnackBar(sharedService.parseError(err.data), SnackBarVariants.Error);
				}
			}
		}

		if (props.appId) {
			GetAddedCarrierAccounts();
		}
	}, [props.appId]);

	useEffect(() => {
		if (!!deletedCarrierId) {
			carriers = carriers.filter((carrier) => carrier.id !== deletedCarrierId);
			setUserCarriersList(userCarriersList.filter((carrier) => carrier.id !== deletedCarrierId));
			updateEligibleCarriers(carriers);
			props.openSnackBar(intlService.GetFormattedMessage('app.snackbar.carrier-deleted-successfully'));
		}
	}, [deletedCarrierId]);

	useEffect(() => {
		const timer = setTimeout(() => searchItems(searchValue), 800);
		return () => clearTimeout(timer);
	}, [searchValue]);

	const updateList = useCallback(
		(data: models.ICarrierAccounts[] | models.ICarrierAccounts) => {
			const existedIndex = carriers.findIndex((element) => element.id === (data as models.ICarrierAccounts).id);
			if (existedIndex !== -1) {
				carriers[existedIndex] = {
					...(data as models.ICarrierAccounts),
				};
			} else {
				const tempList = [...carriers];
				tempList.unshift(...((Array.isArray(data) ? data : [data]) as models.ICarrierAccounts[]));
				carriers = tempList;
			}
			if (!props.fetchAll) {
				updateEligibleCarriers(carriers);
			}
			setUserCarriersList(carriers);
			setSearchValue('');
		},
		[userCarriersList, props.fetchAll]
	);

	const searchItems = useCallback(
		(value: string) => {
			if (!value) {
				setUserCarriersList(carriers);
				return;
			}

			const tempList = carriers.filter((carrier) => {
				const isAppFound = userApps.find(
					(x) => x.appName.toLowerCase().includes(value.toLowerCase()) && x.appId === carrier?.appId
				);

				return carrier.name.toLowerCase().includes(value.toLowerCase()) || isAppFound;
			});
			setUserCarriersList(tempList);
		},
		[userCarriersList]
	);

	const updateEligibleCarriers = useCallback((data: models.ICarrierAccounts[]) => {
		let eligibleCarriers = {};
		data
			.filter((e) => !!e.group)
			.filter((e, index, array) => {
				return index === array.findIndex((x) => x.group === e.group);
			})
			.forEach((e) => {
				eligibleCarriers = {
					...eligibleCarriers,
					[e.group]: e.carrierCode,
				};
			});
		setApplicableCarriers(eligibleCarriers);
	}, []);

	return (
		<React.Fragment>
			<Grid container={true} justifyContent='flex-start' alignItems='center' spacing={2}>
				{/* Add Carrier card */}
				{!isPartnerAccount && (
					<Grid item={true} sm={12} md='auto'>
						<CarrierCardComponent
							isNew={true}
							appId={props.appId}
							onUpdatingCarrier={(data: models.ICarrierAccounts | models.ICarrierAccounts[]) => {
								setRoutedFrom('');
								updateList(data);
							}}
							applicableCarriers={applicableCarriers}
						/>
					</Grid>
				)}
				{/* Search bar */}
				<Grid item={true} sm={12} md={true}>
					<TextField
						fullWidth={true}
						variant='outlined'
						placeholder={
							history.location.pathname === Module.CarrierAccounts.url
								? props.intl.formatMessage({
										id: 'app.carrier-view-search-by-app-name-placeholder',
								  })
								: props.intl.formatMessage({
										id: 'app.carrier-view-search-placeholder',
								  })
						}
						InputProps={{
							startAdornment: (
								<InputAdornment position='start'>
									<Icon name='magnifying-glass' />
								</InputAdornment>
							),
						}}
						value={searchValue}
						onChange={(e) => setSearchValue(e.target.value)}
						size='small'
					/>
				</Grid>
				{/* Added Carriers list */}
				{loading ? (
					<div className='spinner'>
						<CircularProgress size={26} />
					</div>
				) : (
					userCarriersList.map((a: models.ICarrierAccounts, index: number) => {
						return (
							<Grid item={true} key={index} xs={12} sm={12} md={12} lg={12} xl={12}>
								<CarrierCardComponent
									carrier={a}
									appId={props.appId}
									onUpdatingCarrier={updateList}
									onDeletion={setDeletedCarrierId}
									isRoutedFrom={routedFrom}
								/>
							</Grid>
						);
					})
				)}
			</Grid>
		</React.Fragment>
	);
};

export default withSnackBar(injectIntl(withAxiosCancellation(CarrierViewComponent)));



// WEBPACK FOOTER //
// ./src/components/carriers/carrier-view.component.tsx